import { useTranslation } from 'react-i18next'
import type { ReconciliationSession } from '../types'
import { DOC_COLORS } from './doc-constants'
import { PdfPreview } from './PdfPreview'
import { type XmlTarget } from './ValidationSummary'
import {
  type DocColumn,
  truncateFilename, computeDeviations, buildColumns,
} from './documentsPanelHelpers'

// XPaths derived directly from the backend parsers
const TARGETS_XPATHS: Record<string, Omit<XmlTarget, 'label'>> = {
  documentId:  { xpaths: { ordrsp: 'OrderResponse/OrderResponseNumber',  desadv: 'DespatchAdvice/DespatchAdviceNumber', invoice: 'Invoice/InvoiceNumber' } },
  supplier:    { xpaths: { ordrsp: 'OrderResponse/Supplier',             desadv: 'DespatchAdvice/Supplier',             invoice: 'Invoice/Supplier' } },
  supplierIban:{ xpaths: { ordrsp: 'OrderResponse/Supplier/AccountInformation/IBAN', desadv: 'DespatchAdvice/Supplier/AccountInformation/IBAN', invoice: 'Invoice/Supplier/AccountInformation/IBAN' } },
  supplierVat: { xpaths: { ordrsp: 'OrderResponse/Supplier/VATRegistrationNumber',  desadv: 'DespatchAdvice/Supplier/VATRegistrationNumber',  invoice: 'Invoice/Supplier/VATRegistrationNumber' } },
  supplierKvk: { xpaths: { ordrsp: 'OrderResponse/Supplier/ChamberOfCommerceNumber', desadv: 'DespatchAdvice/Supplier/ChamberOfCommerceNumber', invoice: 'Invoice/Supplier/ChamberOfCommerceNumber' } },
  buyer:       { xpaths: { ordrsp: 'OrderResponse/Buyer',                desadv: 'DespatchAdvice/Buyer',                invoice: 'Invoice/Buyer' } },
  buyerVat:    { xpaths: { invoice: 'Invoice/Buyer/VATRegistrationNumber' } },
  invoicer:    { xpaths: { invoice: 'Invoice/Invoicer' } },
  invoicee:    { xpaths: { invoice: 'Invoice/Invoicee' } },
  deliveryParty:{ xpaths: { ordrsp: 'OrderResponse/DeliveryParty',        desadv: 'DespatchAdvice/DeliveryParty',        invoice: 'Invoice/DeliveryParty' } },
  shipFrom:     { xpaths: { desadv: 'DespatchAdvice/ShipFrom', invoice: 'Invoice/ShipFrom' } },
  project:     { xpaths: { ordrsp: 'OrderResponse/ProjectNumber',        desadv: 'DespatchAdvice/ProjectNumber',        invoice: 'Invoice/ProjectNumber' } },
  refOrdrsp:   { xpaths: { desadv: 'DespatchAdvice/OrderResponseNumber', invoice: 'Invoice/OrderResponseNumber' } },
  refDesadv:   { xpaths: { desadv: 'DespatchAdvice/DespatchAdviceNumber', invoice: 'Invoice/DespatchAdviceNumber' } },
  buyersOrderNumber:   { xpaths: { ordrsp: 'OrderResponse/OrderReference/BuyersOrderNumber', desadv: 'DespatchAdvice/BuyersOrderNumber', invoice: 'Invoice/BuyersOrderNumber' } },
  suppliersOrderNumber:{ xpaths: { ordrsp: 'OrderResponse/OrderReference/SuppliersOrderNumber' } },
  endCustomerOrderNumber: { xpaths: { ordrsp: 'OrderResponse/EndCustomerOrderNumber', desadv: 'DespatchAdvice/EndCustomerOrderNumber', invoice: 'Invoice/EndCustomerOrderNumber' } },
}

function devCls(deviant: boolean, base: string): string {
  return deviant ? `${base} text-orange-600 font-semibold` : base
}

function buildGlnNameMap(col: DocColumn): Map<string, string> {
  const pairs: [string | null, string | null][] = [
    [col.supplierGln, col.supplierName],
    [col.buyerGln, col.buyerName],
    [col.invoicerGln, col.invoicerName],
    [col.invoiceeGln, col.invoiceeName],
    [col.deliveryPartyGln, col.deliveryPartyName],
    [col.shipFromGln, col.shipFromName],
  ]
  const map = new Map<string, string>()
  for (const [gln, name] of pairs) {
    if (gln && name) map.set(gln, name)
  }
  return map
}

function resolvePartyName(
  name: string | null,
  gln: string | null,
  glnMap: Map<string, string>,
): { display: string; inferred: boolean } {
  if (name) return { display: name, inferred: false }
  const inferred = gln ? (glnMap.get(gln) ?? null) : null
  return inferred ? { display: inferred, inferred: true } : { display: '—', inferred: false }
}

// ── Grid-helpers ──────────────────────────────────────────────────────────────

function InfoButton({ target, onClick }: { target: XmlTarget; onClick: (t: XmlTarget) => void }) {
  const { t } = useTranslation()
  return (
    <button
      onClick={() => onClick(target)}
      className="opacity-40 hover:opacity-100 transition-opacity shrink-0"
      title={t('documents_panel.show_in_xml', { label: target.label })}
      aria-label={t('documents_panel.show_in_xml', { label: target.label })}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
      </svg>
    </button>
  )
}

function RowLabel({
  children,
  target,
  onInfoClick,
}: {
  children: React.ReactNode
  target?: XmlTarget
  onInfoClick?: (t: XmlTarget) => void
}) {
  return (
    <div className="py-1.5 pr-2 text-xs text-gray-500 dark:text-gray-400 font-medium flex items-center gap-1 pt-2">
      <span>{children}</span>
      {target && onInfoClick && <InfoButton target={target} onClick={onInfoClick} />}
    </div>
  )
}

/** Eén cel in de waarde-kolom (kolommen 2–4). */
function Cell({ empty, children }: { empty?: boolean; children?: React.ReactNode }) {
  const { t } = useTranslation()
  if (empty) return <div className="py-1.5 pt-2 text-xs text-gray-400 dark:text-gray-500 italic">{t('documents_panel.not_provided')}</div>
  return <div className="py-1.5 pt-2">{children}</div>
}

interface Props {
  session: ReconciliationSession
  onInfoClick?: (target: XmlTarget) => void
}

function SentRefsBar({ session }: { session: ReconciliationSession }) {
  const { t } = useTranslation()
  if (!session.sent_at) return null
  const refs = [
    session.sent_or_ref  ? `OR${session.sent_or_ref}`   : null,
    session.sent_da_ref  ? `DA${session.sent_da_ref}`   : null,
    session.sent_inv_ref ? `INV${session.sent_inv_ref}` : null,
  ].filter(Boolean)
  const sentDate = new Date(session.sent_at).toLocaleString('nl-NL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 mb-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 text-xs text-green-800 dark:text-green-300">
      <span className="font-semibold">✓ {t('documents_panel.sent')}</span>
      <span className="text-green-600">{sentDate}</span>
      {refs.map(ref => (
        <span key={ref} className="font-mono bg-white dark:bg-gray-800 border border-green-200 dark:border-green-800/50 rounded px-1.5 py-0.5">{ref}</span>
      ))}
    </div>
  )
}

export function DocumentsPanel({ session, onInfoClick }: Props) {
  const { t } = useTranslation()

  // XPaths met gelokaliseerde labels (binnen component zodat t() beschikbaar is)
  const TARGETS: Record<string, XmlTarget> = {
    documentId:           { label: 'Document ID',                             ...TARGETS_XPATHS.documentId },
    supplier:             { label: t('documents_panel.supplier'),              ...TARGETS_XPATHS.supplier },
    supplierIban:         { label: t('documents_panel.supplier_iban'),         ...TARGETS_XPATHS.supplierIban },
    supplierVat:          { label: t('documents_panel.supplier_vat'),          ...TARGETS_XPATHS.supplierVat },
    supplierKvk:          { label: t('documents_panel.supplier_kvk'),          ...TARGETS_XPATHS.supplierKvk },
    buyer:                { label: t('documents_panel.buyer'),                 ...TARGETS_XPATHS.buyer },
    buyerVat:             { label: t('documents_panel.buyer_vat'),             ...TARGETS_XPATHS.buyerVat },
    invoicer:             { label: 'Invoicer',                                 ...TARGETS_XPATHS.invoicer },
    invoicee:             { label: 'Invoicee',                                 ...TARGETS_XPATHS.invoicee },
    deliveryParty:        { label: 'Delivery Party',                           ...TARGETS_XPATHS.deliveryParty },
    shipFrom:             { label: 'Ship From',                                ...TARGETS_XPATHS.shipFrom },
    project:              { label: 'ProjectNumber',                            ...TARGETS_XPATHS.project },
    refOrdrsp:            { label: 'Ref. OrderResponse',                       ...TARGETS_XPATHS.refOrdrsp },
    refDesadv:            { label: 'Ref. DespatchAdvice',                      ...TARGETS_XPATHS.refDesadv },
    buyersOrderNumber:    { label: t('documents_panel.buyers_order_number'),   ...TARGETS_XPATHS.buyersOrderNumber },
    suppliersOrderNumber: { label: t('documents_panel.suppliers_order_number'),...TARGETS_XPATHS.suppliersOrderNumber },
    endCustomerOrderNumber:{ label: t('documents_panel.end_customer_order_number'), ...TARGETS_XPATHS.endCustomerOrderNumber },
  }

  const cols = buildColumns(session)
  const dev = computeDeviations(cols)
  const glnMaps = cols.map(buildGlnNameMap)

  const hasInvoicer     = cols.some(c => c.invoicerName || c.invoicerGln)
  const hasInvoicee     = cols.some(c => c.invoiceeName || c.invoiceeGln)
  const hasDelivery     = cols.some(c => c.deliveryPartyName || c.deliveryPartyGln)
  const hasShipFrom     = cols.some(c => c.shipFromName || c.shipFromGln)
  const hasOrdrspRef    = cols.some(c => c.refs.some(r => r.label === 'Ref. OrderResponse'))
  const hasDesadvRef    = cols.some(c => c.refs.some(r => r.label === 'Ref. DespatchAdvice'))
  const hasSuppliersOrderNumber   = cols.some(c => c.suppliersOrderNumber)
  const hasEndCustomerOrderNumber = cols.some(c => c.endCustomerOrderNumber)
  const hasPdf          = cols.some(c => c.hasPdf)
  const hasSupplierIban = cols.some(c => c.supplierIban)
  const hasSupplierVat  = cols.some(c => c.supplierVat)
  const hasSupplierKvk  = cols.some(c => c.supplierKvk)
  const hasBuyerVat     = cols.some(c => c.buyerVat)

  const gridCls = 'grid grid-cols-[130px_1fr_1fr_1fr] gap-x-3 divide-y divide-gray-100 dark:divide-gray-700'

  function refValue(col: DocColumn, label: string): string | null {
    return col.refs.find(r => r.label === label)?.value ?? null
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
        {t('documents_panel.offered_files')}
      </h3>
      <SentRefsBar session={session} />

      <div className={gridCls}>
        {/* ── Headerrij: badges ── */}
        <div />
        {cols.map(col => (
          <div key={col.docKey} className="py-1.5">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded border text-xs font-semibold ${DOC_COLORS[col.docKey]}`}>
              {col.label}
            </span>
          </div>
        ))}

        {/* ── Bestand ── */}
        <RowLabel>{t('documents_panel.file')}</RowLabel>
        {cols.map(col => (
          <Cell key={col.docKey} empty={col.filename === null}>
            {col.filename && (
              <p className="text-xs font-mono text-gray-700 dark:text-gray-300 break-all" title={col.filename}>{truncateFilename(col.filename)}</p>
            )}
          </Cell>
        ))}

        {/* ── Document ID ── */}
        <RowLabel target={TARGETS.documentId} onInfoClick={onInfoClick}>Document ID</RowLabel>
        {cols.map(col => (
          <Cell key={col.docKey} empty={col.filename === null}>
            {col.filename && <p className="text-sm text-gray-700 dark:text-gray-300">{col.documentId ?? '—'}</p>}
          </Cell>
        ))}

        {/* ── Leverancier ── */}
        <RowLabel target={TARGETS.supplier} onInfoClick={onInfoClick}>{t('documents_panel.supplier')}</RowLabel>
        {cols.map((col, i) => (
          <Cell key={col.docKey} empty={col.filename === null}>
            {col.filename && (
              <>
                <p className={devCls(dev.supplierName[i], 'text-sm')}>{col.supplierName ?? '—'}</p>
                <p className={devCls(dev.supplierGln[i], 'text-xs font-mono text-gray-500')}>{col.supplierGln ?? ''}</p>
              </>
            )}
          </Cell>
        ))}

        {/* ── Leverancier IBAN (optioneel) ── */}
        {hasSupplierIban && (
          <>
            <RowLabel target={TARGETS.supplierIban} onInfoClick={onInfoClick}>{t('documents_panel.supplier_iban')}</RowLabel>
            {cols.map(col => (
              <Cell key={col.docKey} empty={col.filename === null}>
                {col.filename && <p className="text-xs font-mono text-gray-700 dark:text-gray-300">{col.supplierIban ?? '—'}</p>}
              </Cell>
            ))}
          </>
        )}

        {/* ── Leverancier BTW-nr. (optioneel) ── */}
        {hasSupplierVat && (
          <>
            <RowLabel target={TARGETS.supplierVat} onInfoClick={onInfoClick}>{t('documents_panel.supplier_vat')}</RowLabel>
            {cols.map(col => (
              <Cell key={col.docKey} empty={col.filename === null}>
                {col.filename && <p className="text-xs font-mono text-gray-700 dark:text-gray-300">{col.supplierVat ?? '—'}</p>}
              </Cell>
            ))}
          </>
        )}

        {/* ── Leverancier KvK (optioneel) ── */}
        {hasSupplierKvk && (
          <>
            <RowLabel target={TARGETS.supplierKvk} onInfoClick={onInfoClick}>{t('documents_panel.supplier_kvk')}</RowLabel>
            {cols.map(col => (
              <Cell key={col.docKey} empty={col.filename === null}>
                {col.filename && <p className="text-xs font-mono text-gray-700 dark:text-gray-300">{col.supplierKvk ?? '—'}</p>}
              </Cell>
            ))}
          </>
        )}

        {/* ── Koper ── */}
        <RowLabel target={TARGETS.buyer} onInfoClick={onInfoClick}>{t('documents_panel.buyer')}</RowLabel>
        {cols.map((col, i) => (
          <Cell key={col.docKey} empty={col.filename === null}>
            {col.filename && (
              <>
                <p className={devCls(dev.buyerName[i], 'text-sm')}>{col.buyerName ?? '—'}</p>
                <p className={devCls(dev.buyerGln[i], 'text-xs font-mono text-gray-500')}>{col.buyerGln ?? ''}</p>
              </>
            )}
          </Cell>
        ))}

        {/* ── Koper BTW-nr. (optioneel) ── */}
        {hasBuyerVat && (
          <>
            <RowLabel target={TARGETS.buyerVat} onInfoClick={onInfoClick}>{t('documents_panel.buyer_vat')}</RowLabel>
            {cols.map(col => (
              <Cell key={col.docKey} empty={col.filename === null}>
                {col.filename && <p className="text-xs font-mono text-gray-700 dark:text-gray-300">{col.buyerVat ?? '—'}</p>}
              </Cell>
            ))}
          </>
        )}

        {/* ── Invoicer (optioneel) ── */}
        {hasInvoicer && (
          <>
            <RowLabel target={TARGETS.invoicer} onInfoClick={onInfoClick}>Invoicer</RowLabel>
            {cols.map((col, i) => (
              <Cell key={col.docKey} empty={col.filename === null}>
                {col.filename && (() => {
                  const r = resolvePartyName(col.invoicerName, col.invoicerGln, glnMaps[i])
                  return (
                    <>
                      <p className={`text-sm ${r.inferred ? 'italic text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>{r.display}</p>
                      {col.invoicerGln && <p className="text-xs font-mono text-gray-500 dark:text-gray-400">{col.invoicerGln}</p>}
                    </>
                  )
                })()}
              </Cell>
            ))}
          </>
        )}

        {/* ── Invoicee (optioneel) ── */}
        {hasInvoicee && (
          <>
            <RowLabel target={TARGETS.invoicee} onInfoClick={onInfoClick}>Invoicee</RowLabel>
            {cols.map((col, i) => (
              <Cell key={col.docKey} empty={col.filename === null}>
                {col.filename && (() => {
                  const r = resolvePartyName(col.invoiceeName, col.invoiceeGln, glnMaps[i])
                  return (
                    <>
                      <p className={`text-sm ${r.inferred ? 'italic text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>{r.display}</p>
                      {col.invoiceeGln && <p className="text-xs font-mono text-gray-500 dark:text-gray-400">{col.invoiceeGln}</p>}
                    </>
                  )
                })()}
              </Cell>
            ))}
          </>
        )}

        {/* ── Delivery Party (optioneel) ── */}
        {hasDelivery && (
          <>
            <RowLabel target={TARGETS.deliveryParty} onInfoClick={onInfoClick}>Delivery Party</RowLabel>
            {cols.map((col, i) => (
              <Cell key={col.docKey} empty={col.filename === null}>
                {col.filename && (() => {
                  const r = resolvePartyName(col.deliveryPartyName, col.deliveryPartyGln, glnMaps[i])
                  return (
                    <>
                      <p className={`text-sm ${r.inferred ? 'italic text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>{r.display}</p>
                      {col.deliveryPartyGln && <p className="text-xs font-mono text-gray-500 dark:text-gray-400">{col.deliveryPartyGln}</p>}
                    </>
                  )
                })()}
              </Cell>
            ))}
          </>
        )}

        {/* ── Ship From (optioneel, alleen DA) ── */}
        {hasShipFrom && (
          <>
            <RowLabel target={TARGETS.shipFrom} onInfoClick={onInfoClick}>Ship From</RowLabel>
            {cols.map((col, i) => (
              <Cell key={col.docKey} empty={col.filename === null}>
                {col.filename && (() => {
                  const r = resolvePartyName(col.shipFromName, col.shipFromGln, glnMaps[i])
                  return (
                    <>
                      <p className={`text-sm ${r.inferred ? 'italic text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>{r.display}</p>
                      {col.shipFromGln && <p className="text-xs font-mono text-gray-500 dark:text-gray-400">{col.shipFromGln}</p>}
                    </>
                  )
                })()}
              </Cell>
            ))}
          </>
        )}

        {/* ── Project ── */}
        <RowLabel target={TARGETS.project} onInfoClick={onInfoClick}>Project</RowLabel>
        {cols.map((col, i) => (
          <Cell key={col.docKey} empty={col.filename === null}>
            {col.filename && (
              <>
                {Boolean(col.missingProject) && (
                  <span className="inline-block mb-0.5 px-1 py-0.5 rounded text-xs font-bold bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800/50">{t('documents_panel.error')}</span>
                )}
                <p className={devCls(dev.projectNumber[i], col.missingProject ? 'text-sm text-red-600 font-medium' : 'text-sm')}>
                  {col.projectNumber ?? '—'}
                </p>
              </>
            )}
          </Cell>
        ))}

        {/* ── Ordernr. koper (altijd tonen) ── */}
        <>
          <RowLabel target={TARGETS.buyersOrderNumber} onInfoClick={onInfoClick}>{t('documents_panel.buyers_order_number')}</RowLabel>
          {cols.map(col => (
            <Cell key={col.docKey} empty={col.filename === null}>
              {col.filename && <p className="text-xs font-mono text-gray-700 dark:text-gray-300">{col.buyersOrderNumber ?? '—'}</p>}
            </Cell>
          ))}
        </>

        {/* ── Ordernr. leverancier (optioneel, alleen OR) ── */}
        {hasSuppliersOrderNumber && (
          <>
            <RowLabel target={TARGETS.suppliersOrderNumber} onInfoClick={onInfoClick}>{t('documents_panel.suppliers_order_number')}</RowLabel>
            {cols.map(col => (
              <Cell key={col.docKey} empty={col.filename === null}>
                {col.filename && <p className="text-xs font-mono text-gray-700 dark:text-gray-300">{col.suppliersOrderNumber ?? '—'}</p>}
              </Cell>
            ))}
          </>
        )}

        {/* ── Ordernr. eindklant (optioneel) ── */}
        {hasEndCustomerOrderNumber && (
          <>
            <RowLabel target={TARGETS.endCustomerOrderNumber} onInfoClick={onInfoClick}>{t('documents_panel.end_customer_order_number')}</RowLabel>
            {cols.map(col => (
              <Cell key={col.docKey} empty={col.filename === null}>
                {col.filename && <p className="text-xs font-mono text-gray-700 dark:text-gray-300">{col.endCustomerOrderNumber ?? '—'}</p>}
              </Cell>
            ))}
          </>
        )}

        {/* ── Ref. OrderResponse (optioneel) ── */}
        {hasOrdrspRef && (
          <>
            <RowLabel target={TARGETS.refOrdrsp} onInfoClick={onInfoClick}>Ref. OR</RowLabel>
            {cols.map(col => (
              <Cell key={col.docKey} empty={col.filename === null}>
                {col.filename && (
                  <p className="text-sm text-gray-700 dark:text-gray-300">{refValue(col, 'Ref. OrderResponse') ?? '—'}</p>
                )}
              </Cell>
            ))}
          </>
        )}

        {/* ── Ref. DespatchAdvice (optioneel) ── */}
        {hasDesadvRef && (
          <>
            <RowLabel target={TARGETS.refDesadv} onInfoClick={onInfoClick}>Ref. DA</RowLabel>
            {cols.map(col => (
              <Cell key={col.docKey} empty={col.filename === null}>
                {col.filename && (
                  <p className="text-sm text-gray-700 dark:text-gray-300">{refValue(col, 'Ref. DespatchAdvice') ?? '—'}</p>
                )}
              </Cell>
            ))}
          </>
        )}

        {/* ── PDF Preview (optioneel) ── */}
        {hasPdf && (
          <>
            <RowLabel>PDF</RowLabel>
            {cols.map(col => (
              <Cell key={col.docKey} empty={col.filename === null}>
                {col.filename && col.hasPdf && (
                  <PdfPreview sessionId={session.id} docType={col.docKey} />
                )}
              </Cell>
            ))}
          </>
        )}
      </div>
    </div>
  )
}
